import { NextApiRequest, NextApiResponse } from 'next';
import { GetServerSideProps, GetServerSidePropsContext } from 'next/types';
import { ZodError } from 'zod';
import { logger } from './logger';
import { applyForcedLogoutIfNeeded } from '@/lib/Auth/sessionIntegrity';
import { applyBrandSentryScope } from '@/lib/brand/sentryScope';
import { getBrandLogMetadataFromRequest } from '@lib-server/brand/loggingContext';

export type SsrErrorSeverity = 'info' | 'warning' | 'error' | 'fatal';

export class HttpError<TCode extends number = number> extends Error {
  public readonly cause?: Error;
  public readonly statusCode: TCode;
  public readonly url: string | undefined;
  public readonly method: string | undefined;

  constructor(opts: {
    url?: string;
    method?: string;
    message?: string;
    statusCode: TCode;
    cause?: Error;
  }) {
    super(opts.message ?? `HTTP Error ${opts.statusCode} `);

    Object.setPrototypeOf(this, HttpError.prototype);
    this.name = HttpError.prototype.constructor.name;

    this.cause = opts.cause;
    this.statusCode = opts.statusCode;
    this.url = opts.url;
    this.method = opts.method;

    if (opts.cause instanceof Error && opts.cause.stack) {
      this.stack = opts.cause.stack;
    }
  }

  public static fromRequest(request: Request, response: Response) {
    return new HttpError({
      message: response.statusText,
      url: response.url,
      method: request.method,
      statusCode: response.status,
    });
  }
}

export default class ApiError extends Error {
  public readonly statusCode: number;
  public readonly isOperational: boolean;
  public readonly details?: any;
  public readonly skipSentry?: boolean;
  public readonly headers?: Record<string, string>;

  constructor(
    message: string,
    statusCode: number,
    details?: any,
    isOperational = true,
    skipSentry?: boolean,
    headers?: Record<string, string>
  ) {
    super(message);

    Object.setPrototypeOf(this, ApiError.prototype);
    this.statusCode = statusCode;
    this.isOperational = isOperational;
    this.details = details;
    this.skipSentry = skipSentry;
    this.headers = headers;

    Error.captureStackTrace(this, this.constructor);
  }

  static fromZodError(error: ZodError): ApiError {
    return new this(error.toString(), 400);
  }
}

export function getErrorFromUnknown(
  cause: unknown
): Error & { statusCode?: number; code?: string; skipSentry?: boolean } {
  if (cause instanceof Error) {
    return cause as Error & { statusCode?: number; code?: string };
  }
  if (typeof cause === 'string') {
    return new Error(cause, { cause }) as Error & {
      statusCode?: number;
      code?: string;
    };
  }

  return new Error(`Unhandled error of type '${typeof cause}'`, {
    cause: cause as any,
  }) as Error & { statusCode?: number; code?: string };
}

type ApiErrorLogSeverity = 'info' | 'warn' | 'error';

const EXPECTED_OPERATIONAL_ERROR_PATHS = [
  /^\/api\/session-state\/(?:session|events|events-unauth)(?:[?#]|$)/,
  /^\/api\/assignments\/reports\/create-space(?:\/|[?#]|$)/,
  /^\/api\/(?:organizations\/[^/?#]+\/)?notebook-templates\/ai-access(?:[?#]|$)/,
];

export const getApiErrorLogSeverity = ({
  status,
  url,
  method,
  error,
}: {
  status: number;
  url?: string | null;
  method?: string | null;
  error?: Error & { isOperational?: boolean };
}): ApiErrorLogSeverity => {
  if (status >= 500) return 'error';

  const pathname = url?.split('?')[0] ?? '';
  const isOperational = error?.isOperational !== false;
  const isExpectedMissingNotebookRead =
    method?.toUpperCase() === 'GET' &&
    status === 404 &&
    /^\/api\/notebooks\/[^/?#]+$/.test(pathname);

  if (isExpectedMissingNotebookRead && isOperational) {
    return 'warn';
  }

  const isExpectedOperationalPath = EXPECTED_OPERATIONAL_ERROR_PATHS.some(
    (pattern) => pattern.test(pathname)
  );
  const isExpectedStatus = status === 401 || status === 403 || status === 409;

  if (isExpectedOperationalPath && isExpectedStatus && isOperational) {
    if (status === 409 && /Session expired/i.test(error?.message ?? '')) {
      return 'info';
    }
    return 'warn';
  }

  return 'error';
};

export const handleApiError = async (
  error: unknown,
  req: NextApiRequest,
  res: NextApiResponse
) => {
  const err = getErrorFromUnknown(error);
  const status =
    (err as ApiError).statusCode ||
    (typeof (err as any).status === 'number' && (err as any).status) ||
    500;
  const sessionIntegrityIssue = applyForcedLogoutIfNeeded(req, res);
  const logSeverity = getApiErrorLogSeverity({
    status,
    url: req.url,
    method: req.method,
    error: err,
  });

  const logContext = {
    feature: 'api',
    action: logSeverity === 'error' ? 'error' : 'expected_operational_error',
    resource: req.url,
    metadata: {
      ...getBrandLogMetadataFromRequest(req),
      method: req.method,
      status,
      errorMessage: err.message,
      errorName: err.name,
      forceLogout: Boolean(sessionIntegrityIssue),
      forceLogoutReason: sessionIntegrityIssue?.reason,
      forceLogoutSource: sessionIntegrityIssue?.source,
    },
  };

  if (logSeverity === 'error') {
    logger.error('API error', logContext, err);
  } else if (logSeverity === 'warn') {
    logger.warn('API expected operational error', logContext);
  } else {
    logger.info('API expected operational error', logContext);
  }

  const isProd = process.env.NODE_ENV === 'production';
  const existingDetails = (err as ApiError).details;
  const details =
    sessionIntegrityIssue === null
      ? existingDetails
      : {
          ...(existingDetails &&
          typeof existingDetails === 'object' &&
          !Array.isArray(existingDetails)
            ? existingDetails
            : {
                ...(typeof existingDetails === 'undefined'
                  ? {}
                  : { originalDetails: existingDetails }),
              }),
          auth: {
            forceLogout: true,
            reason: sessionIntegrityIssue.reason,
            source: sessionIntegrityIssue.source,
            detectedAt: sessionIntegrityIssue.detectedAt,
          },
        };

  const response = {
    message: err.message,
    statusCode: status,
    details,
    isOperational: (err as ApiError).isOperational,
    ...(isProd ? {} : { stack: err.stack }),
  };

  const headers = (err as ApiError).headers;
  if (headers && typeof headers === 'object') {
    Object.entries(headers).forEach(([key, value]) => {
      res.setHeader(key, value);
    });
  }

  return res.status(status).json(response);
};

export const handleSsrError = async (
  error: unknown,
  options?: {
    feature?: string;
    severity?: SsrErrorSeverity;
    skipSentry?: boolean;
    context?: GetServerSidePropsContext;
    isSsg?: boolean;
  }
) => {
  const err = getErrorFromUnknown(error);
  logger.error('SSR error caught', {
    feature: options?.feature ?? 'ssr',
    action: 'ssr_error',
    metadata: {
      ...(options?.context
        ? getBrandLogMetadataFromRequest(options.context.req)
        : {}),
      severity: options?.severity,
      isSsg: options?.isSsg,
    },
  });

  if (!options?.skipSentry) {
    try {
      const sentry = await import('@sentry/nextjs');
      const brandMetadata = options?.context
        ? getBrandLogMetadataFromRequest(options.context.req)
        : null;
      if (brandMetadata) {
        sentry.withScope((scope) => {
          applyBrandSentryScope(scope, brandMetadata);
          sentry.captureException(err);
        });
      } else {
        sentry.captureException(err);
      }
    } catch {
      // Sentry not available or disabled; ignore
    }
  }
};

export const withSsrErrorHandling =
  <P extends { [key: string]: any }>(
    handler: GetServerSideProps<P>,
    options?: {
      feature?: string;
      skipSentry?: boolean;
      severity?: SsrErrorSeverity;
      onErrorReturn?:
        | Awaited<ReturnType<GetServerSideProps<P>>>
        | (() => Awaited<ReturnType<GetServerSideProps<P>>>);
    }
  ): GetServerSideProps<P> =>
  async (context) => {
    try {
      return await handler(context);
    } catch (error) {
      await handleSsrError(error, {
        feature: options?.feature,
        skipSentry: options?.skipSentry,
        severity: options?.severity,
        context,
      });
      const fallback =
        typeof options?.onErrorReturn === 'function'
          ? options.onErrorReturn()
          : options?.onErrorReturn;
      return (
        fallback ?? {
          redirect: { destination: '/500', permanent: false },
        }
      );
    }
  };
